import { z } from "zod/v4";
import { CreatedAt, UpdatedAt } from "../system";
import { BrandIdSchema, ItemId, ItemIdSchema, SaleIdSchema, SpaceBGMIdSchema } from "../../id";
import { DocumentSnapshotLike } from "../../firestore";
import { BrandDenormalized } from "./brand";
import { SpaceBackgroundCommon } from "../space-background";
import { SaleItemDenormalized } from "./sale"
import { SpaceBGMDocumentCommon } from "../space-bgm"
import { DocumentStatus, Media, MediaImage, TagsRequired } from "../../common"
import { ItemKind, ItemKindLook, ItemKindSpaceBackground, ItemKindSpaceBGM } from "./item-kind"
import { AssetBundleFile, UploadAssetBundleRequest } from "./file"
import { assertNever } from "../../lib/assert-never"

/**
 * 全アイテムに共通の項目。
 */
export const ItemCommon = z.object({
  name: z.string().describe("アイテムの名前。"),
  description: z.string().describe("アイテムの商品販売時デフォルトテキスト。販売時にデフォルトでこの値が入力欄に入力される。"),
  brands: z.array(BrandDenormalized).min(0).max(3).describe("非正規化されたブランドの情報。マスターデータの更新直後は古い情報が入っているので注意。"),
  brand_ids: z.array(BrandIdSchema).min(0).max(3).describe("ブランドIDの配列。Firestoreクエリ最適化のために使用。"),
  default_price: z.number().min(0).describe("アイテムの'標準'価格。この価格は実際の販売に使われるわけではなく、あくまで基本の値を示すものであり、ユーザー向けの価格としては販売側のデータが使われるので注意。"),
  media: z.object({
    first: MediaImage.describe("アイテムの詳細を示す画像。各所のサムネイルとして表示される。動画は入らず、画像のみが登録される。value には CDN URL（大サイズ: w-1080,h-1080,q-80,f-webp）が格納される。variants には同一画像の CDN バリアント URL が格納される（例: w-512h-512q-80f-webp=サムネイル、w-1080h-1080q-80f-webp=大サイズ）。"),
    detail: z.array(Media).nullable().describe("アイテムの詳細を示す画像・動画。詳細ページに first と一緒に表示される。動画が登録されうる。画像の場合は value に CDN URL（大サイズ: w-1080,h-1080,q-80,f-webp）、variants にサムネイル/大サイズの CDN バリアント URL が格納される。"),
    share: MediaImage.nullable().describe("アイテムの共有画像となるベースの画像。設定されている場合、OGPとして表示される画像のベースとして使用される画像が変更される。value に CDN URL（大サイズ: w-1080,h-1080,q-80,f-webp）、variants にサムネイル/大サイズの CDN バリアント URL が格納される。"),
  }).describe("各所で表示される画像や動画。"),
  sales: z.array(z.object({
    id: SaleIdSchema,
    value: SaleItemDenormalized,
  })).describe("セールの情報。ただし、全商品、アイテム種別のセールはここに入らないので注意すること。"),
  sale_ids: z.array(SaleIdSchema).describe("セールIDの配列。Firestoreクエリ最適化のために使用。"),
  tags: TagsRequired,
  related_item_ids: z.array(ItemIdSchema).nullable().describe("関連アイテムのID。"),
  status: DocumentStatus,
  planning_management_id: z.string().nullable().describe("企画管理用の Uniq ID（管理番号）。最大12文字。AES-CBC で暗号化された文字列が格納される。").optional(),
  planning_name: z.string().nullable().describe("企画管理用名称。最大50文字。AES-CBC で暗号化された文字列が格納される。").optional(),
})
export type ItemCommon = z.infer<typeof ItemCommon>

export type ItemVariantLook = z.infer<typeof ItemVariantLook>
export const ItemVariantLook = z.object({
  kind: ItemKindLook,
  media_account: z.array(MediaImage).nullable().describe("ルックのアカウントTOP用画像"),
  asset_bundle: AssetBundleFile.nullable(),
})

export type ItemVariantSpaceBackground = z.infer<typeof ItemVariantSpaceBackground>
export const ItemVariantSpaceBackground =
  SpaceBackgroundCommon.extend({
  kind: ItemKindSpaceBackground,
  asset_bundle: AssetBundleFile.nullable(),
  dress_codes: z.object({
    tags: TagsRequired,
    look_ids: z.array(ItemIdSchema).min(1).max(5).describe("試着ルックID一覧"),
  }).nullable().describe("ドレスコード設定。"),
})

export type ItemVariantSpaceBGM = z.infer<typeof ItemVariantSpaceBGM>
export const ItemVariantSpaceBGM =
  SpaceBGMDocumentCommon.extend({
    kind: ItemKindSpaceBGM,
})


export const ItemVariant = z.union([
  ItemVariantLook,
  ItemVariantSpaceBackground,
  ItemVariantSpaceBGM,
])

export const ItemInventoryVariant = z.union([
  ItemVariantLook,
  ItemVariantSpaceBackground,
  ItemVariantSpaceBGM,
]).describe("インベントリ用の拡張型。VRMの使用回数などが入っている。")

export const ItemDocumentMeta = z.intersection(
  ItemCommon,
  ItemVariant,
)
export type ItemDocumentMeta = z.infer<typeof ItemDocumentMeta>

export type CreateItemRequest = z.infer<typeof CreateItemRequest>
export const CreateItemRequest = z.intersection(
  ItemCommon.pick({
    name: true,
    description: true,
    // brands: true,
    // default_price: true, // snake to camel
    // media: true,
    // sales: false,
    status: true,
    tags: true,
  }).extend({
    name: z.string().min(1),
    defaultPrice: z.coerce.number(),
    brandIds: z.array(BrandIdSchema).min(0).max(3),
    mediaFirstLocation: z.string(),
    mediaDetailLocations: z.array(z.string()).optional(),
    mediaShareLocation: z.string().optional(),
    planningManagementId: z.string().nullable().optional(),
    planningName: z.string().nullable().optional(),
  }),
  z.union([
    // look
    ItemVariantLook.pick({
      kind: true,
      // asset_bundle: true, // media
      // media_account: true, // media
    }),

    // background
    ItemVariantSpaceBackground.pick({
      kind: true,
      // atmosphere_text: true, // snake to camel
      // capacity: true, // coarse
      // recommend_bgm: true, // snake to camel
      // asset_bundle: true, // media
      // thumbnail_url: true, // media
      // images: true, // media
      // dress_codes: true, // snake to camel
    }).extend({
      capacity: z.coerce.number(),
      atmosphereText: z.string(),
      recommendBGM: SpaceBGMIdSchema.optional(),
      dressCodes: z.object({
        tags: TagsRequired,
        lookIds: z.array(ItemIdSchema).min(1).max(5).describe("試着ルックID一覧"),
      }).nullable().describe("ドレスコード設定。"),
    }),

    // BGM
    ItemVariantSpaceBGM.pick({
      kind: true,
      // default_volume: true, // snake to camel
      // trial_range: true, // snake to camel
      // url: true, // media
    }).extend({
      defaultVolume: z.coerce.number().min(0).max(100),
      trialRange: z.object({
        startAt: z.number(),
        endAt: z.number(),
      }).optional(),
    }),
  ])
)

export const UpdateItemRequest = z.object({
  name: z.string().optional(),
  description: z.string().optional(),
  brandIds: z.array(BrandIdSchema).min(0).max(3).optional(),
  defaultPrice: z.number().optional(),
  status: DocumentStatus.optional(),
  tags: TagsRequired.optional(),
  mediaFirstLocation: z.string().optional(),
  mediaDetailLocations: z.array(z.string()).optional(),
  mediaShareLocation: z.string().optional(),
  planningManagementId: z.string().nullable().optional(),
  planningName: z.string().nullable().optional(),
  uploadAssetBundle: UploadAssetBundleRequest.optional(),
  uploadMediaAccount: z.boolean().optional(),
  uploadThumbnail: z.boolean().optional(),
  uploadHomeImage: z.boolean().optional(),
  uploadBGM: z.boolean().optional(),
  capacity: z.number().optional(),
  atmosphereText: z.string().optional(),
  recommendBGM: SpaceBGMIdSchema.optional(),
  dressCodes: z.object({
    tags: TagsRequired,
    lookIds: z.array(ItemIdSchema).min(1).max(5).describe("試着ルックID一覧"),
  }).optional().nullable().describe("ドレスコード設定。"),
  defaultVolume: z.number().min(0).max(100).optional(),
  trialRange: z.object({
    startAt: z.number(),
    endAt: z.number(),
  }).optional(),
})

export type UpdateItemRequest = z.infer<typeof UpdateItemRequest>

export const ItemDocumentBody = z.intersection(
  ItemDocumentMeta,
  z.object({
    created_at: CreatedAt,
    updated_at: UpdatedAt,
  })
)

export const ItemDocument = z.intersection(
  ItemDocumentBody,
  z.object({
    id: ItemIdSchema,
  })
)

export type ItemDocument = z.infer<typeof ItemDocument>

export const ItemInventoryDenormalized = z.intersection(
  ItemInventoryVariant,
  ItemCommon.pick({
    name: true,
    description: true,
    brands: true,
    default_price: true,
    media: true,
    // sales: false,
    status: true,
    tags: true,
  })
).describe("インベントリ向けに非正規化されたアイテムの情報。マスターデータの更新直後は古い情報が入っているので注意。")
export type ItemInventoryDenormalized = z.infer<typeof ItemInventoryDenormalized>

export class ItemDocuments {

  static toDocumentData(doc: ItemDocument): object {
    return {
      ...doc,
      // id は document path に入るので入れない
      id: undefined,
    };
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): ItemDocument | null {
    if (!doc) {
      return null;
    }
    const data = doc.data();
    if (data === undefined) {
      return null;
    }

    const kind = data["kind"] as ItemKind;
    const common: ItemCommon = {
      name: data["name"],
      description: data["description"],
      brands: data["brands"],
      brand_ids: data["brand_ids"],
      default_price: data["default_price"],
      media: data["media"],
      sales: data["sales"],
      sale_ids: data["sale_ids"],
      tags: data["tags"],
      related_item_ids: data["related_item_ids"],
      status: data["status"],
      planning_management_id: data["planning_management_id"] ?? null,
      planning_name: data["planning_name"] ?? null,
    }

    const meta = {
      ...common,
      id: ItemId(doc.id),
      value: data["value"],
      created_at: data["created_at"],
      updated_at: data["updated_at"],
    }

    if (kind === "look") {
      return {
        ...meta,
        kind,
        media_account: data["media_account"],
        asset_bundle: data["asset_bundle"],
      }
    }

    if (kind === "space-background") {
      const rawImages = data["images"] ?? { home: [] }
      const home: MediaImage[] = (rawImages.home ?? []).map((img: unknown) => {
        if (typeof img === "string") {
          // レガシー形式: gs:// URL 文字列 → MediaImage に変換
          return {
            kind: "image" as const,
            value: "",
            location: img,
            updated_at: 0,
          }
        }
        if (typeof img === "object" && img !== null && "kind" in img) {
          return img as MediaImage
        }
        // 想定外の形式: フォールバック
        return {
          kind: "image" as const,
          value: "",
          location: "",
          updated_at: 0,
        }
      })

      return {
        ...meta,
        kind,
        capacity: data["capacity"],
        atmosphere_text: data["atmosphere_text"],
        recommend_bgm: data["recommend_bgm"],
        asset_bundle: data["asset_bundle"],
        dress_codes: data["dress_codes"],
        thumbnail_url: data["thumbnail_url"],
        images: { home },
        can_delete: data["can_delete"],
      }
    }

    if (kind === "space-bgm") {
      return {
        ...meta,
        kind,
        default_volume: data["default_volume"],
        trial_range: data["trial_range"],
        url: data["url"],
        can_delete: data["can_delete"],
      }
    }

    throw new Error(`Unknown item kind: ${kind as string}`)
  }

  static toInventoryDenormalized(item: ItemDocument): ItemInventoryDenormalized {
    const common = {
      name: item.name,
      description: item.description,
      brands: item.brands,
      default_price: item.default_price,
      media: item.media,
      status: item.status,
      tags: item.tags,
    }

    if (item.kind === "look") {
      return {
        ...common,
        kind: item.kind,
        media_account: item.media_account,
        asset_bundle: item.asset_bundle
      }
    }

    if (item.kind === "space-background") {
      return {
        ...common,
        kind: item.kind,
        capacity: item.capacity,
        atmosphere_text: item.atmosphere_text,
        recommend_bgm: item.recommend_bgm,
        asset_bundle: item.asset_bundle,
        dress_codes: item.dress_codes,
        thumbnail_url: item.thumbnail_url,
        images: item.images,
        can_delete: item.can_delete
      }
    }

    if (item.kind === "space-bgm") {
      return {
        ...common,
        kind: item.kind,
        default_volume: item.default_volume,
        trial_range: item.trial_range,
        url: item.url,
        can_delete: item.can_delete
      }
    }

    return assertNever(item)
  }
}
